import { useState, useRef, useEffect, useCallback } from "react";
import SendIcon from "@mui/icons-material/Send";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Header from "../../components/Header";
import { SessionSidebar } from "../../components/Copilot/SessionSidebar";
import { ModelPicker } from "../../components/Copilot/ModelPicker";
import { TokenMeter } from "../../components/Copilot/TokenMeter";
import { MessageList } from "../../components/Copilot/MessageList";
import { SuggestionChips } from "../../components/Copilot/SuggestionChips";
import { getAccessToken } from "../../common/apiClient";
import { getStoredTenantId } from "../../common/auth";
import { getCopilotWsUrl } from "../../config/copilotConfig";
import {
  readInitialCopilotState,
  saveSessionMessages,
  loadSessionMessages,
  loadSessions,
  setActiveSessionId,
  getActiveSessionId,
  saveSessionMeta,
} from "../../lib/copilotSessionStorage";
import {
  fetchCopilotSessions,
  fetchCopilotSession,
  fetchCopilotUserUsage,
} from "../../lib/copilotGraphql";
import {
  canUseCache,
  getCachedResponse,
  setCachedResponse,
  invalidateCache,
} from "../../lib/copilotResponseCache";
import { useCopilotFeature } from "../../hooks/useCopilotFeature";
import { CopilotUpsellModal } from "../../components/Copilot/CopilotUpsell";
import "./Copilot.css";

const WS_URL = getCopilotWsUrl();
const SCROLL_THRESHOLD = 80;

function newSessionId() {
  return `sess_${crypto.randomUUID().slice(0, 8)}`;
}

export default function Copilot() {
  const { enabled: copilotEnabled, plan, loading: copilotLoading } = useCopilotFeature();
  const [upsellOpen, setUpsellOpen] = useState(false);
  const initial = useRef(readInitialCopilotState(newSessionId)).current;

  const [messages, setMessages] = useState(initial.messages);
  const [input, setInput] = useState("");
  const [model, setModel] = useState(initial.model);
  const [streaming, setStreaming] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [sessionId, setSessionId] = useState(initial.sessionId);
  const [sessionTokens, setSessionTokens] = useState(initial.sessionTokens);
  const [sidebarSessions, setSidebarSessions] = useState([]);
  const [userUsage, setUserUsage] = useState(null);
  const wsRef = useRef(null);
  const streamTimeoutRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const modelRef = useRef(model);
  const sessionTokensRef = useRef(sessionTokens);
  const messagesContainerRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastQueryRef = useRef({ message: "", eligible: false });
  const messagesRef = useRef(messages);
  sessionIdRef.current = sessionId;
  modelRef.current = model;
  sessionTokensRef.current = sessionTokens;
  messagesRef.current = messages;

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    setActiveSessionId(sessionId);
  }, [sessionId]);

  useEffect(() => {
    saveSessionMessages(sessionId, messages);
  }, [sessionId, messages]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickToBottomRef.current = dist < SCROLL_THRESHOLD;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom(messages.length <= 2 ? "auto" : "smooth");
  }, [messages, streaming, scrollToBottom]);

  useEffect(() => {
    const anchor = scrollAnchorRef.current;
    if (!anchor || !stickToBottomRef.current) return undefined;
    const ro = new ResizeObserver(() => scrollToBottom("auto"));
    ro.observe(anchor);
    return () => ro.disconnect();
  }, [messages, scrollToBottom]);

  const clearStreamTimeout = useCallback(() => {
    if (streamTimeoutRef.current) {
      clearTimeout(streamTimeoutRef.current);
      streamTimeoutRef.current = null;
    }
  }, []);

  const persistSessionMeta = useCallback((extra = {}) => {
    const tokens = sessionTokensRef.current;
    const msgs = messagesRef.current;
    const firstUser = msgs.find((x) => x.role === "user");
    saveSessionMeta(sessionIdRef.current, {
      title: firstUser?.content?.slice(0, 80) || "Conversación",
      tokens: tokens.input + tokens.output,
      sessionTokens: tokens,
      model: modelRef.current,
      ...extra,
    });
  }, []);

  const refreshSidebarSessions = useCallback(async () => {
    try {
      const items = await fetchCopilotSessions(30);
      setSidebarSessions(items);
      return items;
    } catch (err) {
      console.warn("No se pudo cargar sesiones del copiloto:", err);
      return [];
    }
  }, []);

  const applyServerSession = useCallback((payload) => {
    if (!payload) return;
    setMessages(payload.messages);
    setSessionTokens(payload.sessionTokens);
    setModel(payload.model);
    saveSessionMeta(payload.summary.id, {
      title: payload.summary.title,
      model: payload.summary.model,
      tokens: payload.summary.tokens,
      sessionTokens: payload.sessionTokens,
    });
    saveSessionMessages(payload.summary.id, payload.messages);
  }, []);

  const loadSessionFromServer = useCallback(
    async (id) => {
      try {
        const payload = await fetchCopilotSession(id);
        if (payload) {
          applyServerSession(payload);
          return true;
        }
      } catch (err) {
        console.warn("No se pudo cargar sesión del servidor:", err);
      }
      const meta = loadSessions()[id] || {};
      setMessages(loadSessionMessages(id));
      setSessionTokens(meta.sessionTokens || { input: 0, output: 0, cost: 0 });
      setModel(meta.model || "haiku");
      return false;
    },
    [applyServerSession]
  );

  const refreshSidebarRef = useRef(refreshSidebarSessions);
  refreshSidebarRef.current = refreshSidebarSessions;

  const failStreaming = useCallback((markdown) => {
    clearStreamTimeout();
    setStreaming(false);
    setMessages((m) => {
      const last = m[m.length - 1];
      if (last?.role === "assistant" && last.streaming) {
        return [
          ...m.slice(0, -1),
          {
            ...last,
            streaming: false,
            renderSpec: {
              blocks: [{ type: "callout", tone: "warning", markdown }],
            },
          },
        ];
      }
      return [
        ...m,
        {
          role: "assistant",
          content: "",
          streaming: false,
          renderSpec: {
            blocks: [{ type: "callout", tone: "warning", markdown }],
          },
        },
      ];
    });
  }, [clearStreamTimeout]);

  const applyCachedResponse = useCallback((cached) => {
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content: cached.content || "",
        renderSpec: cached.renderSpec,
        tools: cached.tools || [],
        usage: cached.usage,
        fromCache: true,
        streaming: false,
      },
    ]);
    if (cached.usage) {
      setSessionTokens((t) => ({
        input: t.input,
        output: t.output,
        cost: t.cost,
      }));
    }
    setStreaming(false);
    clearStreamTimeout();
    stickToBottomRef.current = true;
    scrollToBottom("auto");
  }, [clearStreamTimeout, scrollToBottom]);

  const handleEvent = useCallback((evt) => {
    if (evt.event === "token") {
      setMessages((m) => {
        const last = m[m.length - 1];
        if (last?.role === "assistant" && last.streaming) {
          return [...m.slice(0, -1), { ...last, content: last.content + evt.text }];
        }
        return [...m, { role: "assistant", content: evt.text, streaming: true, tools: [] }];
      });
    } else if (evt.event === "tool") {
      setMessages((m) => {
        const last = m[m.length - 1];
        const tools = [...(last?.tools || []), { name: evt.name }];
        return last?.role === "assistant"
          ? [...m.slice(0, -1), { ...last, tools }]
          : [...m, { role: "assistant", content: "", streaming: true, tools }];
      });
    } else if (evt.event === "done") {
      const query = lastQueryRef.current;
      setMessages((m) => {
        const last = m[m.length - 1];
        const assistantMsg = {
          role: "assistant",
          content: last?.content || "",
          streaming: false,
          tools: last?.tools || [],
          renderSpec: evt.render_spec,
          usage: evt.usage,
        };
        if (query.eligible) {
          const tenantId = getStoredTenantId() || "default";
          setCachedResponse(tenantId, query.message, modelRef.current, {
            content: assistantMsg.content,
            renderSpec: evt.render_spec,
            tools: assistantMsg.tools,
            usage: evt.usage,
          });
        }
        return [...m.slice(0, -1), assistantMsg];
      });
      setSessionTokens((t) => ({
        input: t.input + (evt.usage?.input_tokens || 0),
        output: t.output + (evt.usage?.output_tokens || 0),
        cost: +(t.cost + (evt.usage?.cost_usd || 0)).toFixed(5),
      }));
      setTimeout(() => {
        persistSessionMeta();
        refreshSidebarRef.current();
        fetchCopilotUserUsage()
          .then((usage) => usage && setUserUsage(usage))
          .catch(() => {});
      }, 0);
      setStreaming(false);
      clearStreamTimeout();
    }
  }, [clearStreamTimeout, persistSessionMeta]);

  const handleEventRef = useRef(handleEvent);
  handleEventRef.current = handleEvent;

  const connectWebSocket = useCallback(async () => {
    if (!copilotEnabled) {
      return { ws: null, error: "" };
    }
    if (!WS_URL) {
      const err = "No se pudo conectar al copiloto (WebSocket).";
      setConnectError(err);
      setConnected(false);
      return { ws: null, error: err };
    }
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return { ws: wsRef.current, error: "" };
    }
    if (wsRef.current?.readyState === WebSocket.CONNECTING) {
      return new Promise((resolve) => {
        const ws = wsRef.current;
        const finish = (result) => {
          ws.removeEventListener("open", onOpen);
          ws.removeEventListener("close", onClose);
          ws.removeEventListener("error", onClose);
          resolve(result);
        };
        const onOpen = () => finish({ ws, error: "" });
        const onClose = () =>
          finish({
            ws: null,
            error: "No se pudo abrir el WebSocket. Verificá tu sesión e intentá de nuevo.",
          });
        ws.addEventListener("open", onOpen);
        ws.addEventListener("close", onClose);
        ws.addEventListener("error", onClose);
      });
    }

    try {
      setConnectError("");
      const idToken = await getAccessToken();
      return await new Promise((resolve) => {
        let settled = false;
        const finish = (result) => {
          if (settled) return;
          settled = true;
          resolve(result);
        };
        const ws = new WebSocket(`${WS_URL}?idToken=${encodeURIComponent(idToken)}`);
        ws.onopen = () => {
          setConnected(true);
          setConnectError("");
          finish({ ws, error: "" });
        };
        ws.onclose = (event) => {
          setConnected(false);
          let err = "";
          if (event.code === 1008 || event.code === 4401 || event.code === 401 || event.code === 1006) {
            err = event.code === 1006
              ? "El copiloto no está disponible en tu plan."
              : "Sesión rechazada por el servidor. Volvé a iniciar sesión.";
          } else if (event.code !== 1000) {
            err = "No se pudo mantener la conexión WebSocket con el copiloto.";
          }
          if (err) setConnectError(err);
          finish({ ws: null, error: err });
        };
        ws.onmessage = (e) => {
          try {
            handleEventRef.current(JSON.parse(e.data));
          } catch (err) {
            console.error("WS parse error", err);
          }
        };
        ws.onerror = () => {
          setConnected(false);
          const err = "Error de conexión con el copiloto.";
          setConnectError(err);
          finish({ ws: null, error: err });
        };
        wsRef.current = ws;
      });
    } catch (err) {
      console.error("WS connect error", err);
      setConnected(false);
      const msg = "No hay sesión activa. Iniciá sesión en la plataforma.";
      setConnectError(msg);
      return { ws: null, error: msg };
    }
  }, [copilotEnabled]);

  const openUpsellIfDisabled = useCallback(() => {
    if (copilotLoading) return false;
    if (!copilotEnabled) {
      setUpsellOpen(true);
      return true;
    }
    return false;
  }, [copilotEnabled, copilotLoading]);

  useEffect(() => {
    if (!copilotEnabled) {
      return undefined;
    }
    let cancelled = false;

    async function hydrate() {
      const serverSessions = await refreshSidebarSessions();
      if (cancelled) return;

      try {
        const usage = await fetchCopilotUserUsage();
        if (!cancelled && usage) setUserUsage(usage);
      } catch {
        /* optional */
      }

      const activeId = getActiveSessionId() || initial.sessionId;
      const onServer = serverSessions.some((s) => s.id === activeId);
      if (onServer) {
        await loadSessionFromServer(activeId);
        if (!cancelled) setSessionId(activeId);
      } else if (initial.messages.length > 0) {
        setSessionId(activeId);
      } else if (serverSessions.length > 0) {
        const first = serverSessions[0].id;
        if (!cancelled) {
          setSessionId(first);
          setActiveSessionId(first);
          await loadSessionFromServer(first);
        }
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [copilotEnabled, initial.sessionId, initial.messages.length, loadSessionFromServer, refreshSidebarSessions]);

  useEffect(() => {
    if (!copilotEnabled) {
      return undefined;
    }
    connectWebSocket();
    return () => {
      clearStreamTimeout();
      wsRef.current?.close();
    };
  }, [connectWebSocket, clearStreamTimeout, copilotEnabled]);

  const deliverAssistant = useCallback(
    (msg, { forceRefresh = false } = {}) => {
      setMessages((m) => [...m, { role: "assistant", content: "", streaming: true, tools: [] }]);
      stickToBottomRef.current = true;
      scrollToBottom("auto");

      connectWebSocket().then(({ ws, error }) => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(
            JSON.stringify({
              action: "message",
              message: msg,
              session_id: sessionIdRef.current,
              model: modelRef.current,
              force_refresh: forceRefresh,
            })
          );
        } else {
          failStreaming(
            error ||
              (!WS_URL
                ? "Configurá `REACT_APP_COPILOT_WS_URL` en `.env.local` y reiniciá `npm start`."
                : "No se pudo abrir el WebSocket. Verificá tu sesión e intentá de nuevo.")
          );
        }
      });
    },
    [connectWebSocket, failStreaming, scrollToBottom]
  );

  const sendRef = useRef(null);

  const send = async (text, { forceRefresh = false } = {}) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    if (openUpsellIfDisabled()) return;

    const priorMessages = messagesRef.current;
    const cacheEligible = !forceRefresh && canUseCache(priorMessages);
    const tenantId = getStoredTenantId() || "default";

    setMessages((m) => [...m, { role: "user", content: msg, createdAt: Date.now() }]);
    setStreaming(true);
    setInput("");
    stickToBottomRef.current = true;
    scrollToBottom("auto");
    clearStreamTimeout();
    streamTimeoutRef.current = setTimeout(() => {
      failStreaming(
        "**Tiempo de espera agotado.** El agente puede estar procesando tools o Bedrock tardó demasiado. Intentá de nuevo con el modelo Haiku."
      );
    }, 125000);

    saveSessionMeta(sessionId, {
      title: msg.slice(0, 80),
      model,
      sessionTokens,
    });

    if (cacheEligible) {
      const cached = getCachedResponse(tenantId, msg, model);
      if (cached) {
        applyCachedResponse(cached);
        persistSessionMeta();
        return;
      }
    }

    lastQueryRef.current = { message: msg, eligible: cacheEligible };
    deliverAssistant(msg, { forceRefresh });
  };
  sendRef.current = send;

  const handleRefreshCached = useCallback((assistantMsg, index) => {
    if (openUpsellIfDisabled()) return;
    const userMsg = messagesRef.current[index - 1];
    if (!userMsg || userMsg.role !== "user") return;
    setMessages((m) => m.slice(0, index - 1));
    sendRef.current(userMsg.content, { forceRefresh: true });
  }, [openUpsellIfDisabled]);

  const selectSession = async (id) => {
    if (id === sessionId) return;
    setSessionId(id);
    setActiveSessionId(id);
    setStreaming(false);
    clearStreamTimeout();
    stickToBottomRef.current = true;
    await loadSessionFromServer(id);
  };

  const startNewSession = () => {
    const id = newSessionId();
    setSessionId(id);
    setActiveSessionId(id);
    setMessages([]);
    setSessionTokens({ input: 0, output: 0, cost: 0 });
    setStreaming(false);
    clearStreamTimeout();
    stickToBottomRef.current = true;
  };

  const handleInvalidateCache = () => {
    invalidateCache(getStoredTenantId() || "default");
  };

  const lastUsage = messages[messages.length - 1]?.usage;
  const isEmpty = messages.length === 0;

  return (
    <div className="cop-shell">
      <Header />
      <div className="cop-layout">
        <SessionSidebar
          activeId={sessionId}
          onSelect={selectSession}
          onNew={startNewSession}
          sessions={sidebarSessions}
        />
        <main className="cop-main">
          <div className="cop-toolbar">
            <ModelPicker value={model} onChange={setModel} disabled={streaming} />
            <TokenMeter session={sessionTokens} lastUsage={lastUsage} userUsage={userUsage} />
            <button
              type="button"
              className="cop-cache-invalidate"
              onClick={handleInvalidateCache}
              title="Borrar respuestas en caché de esta organización"
            >
              Actualizar datos
            </button>
            {WS_URL && copilotEnabled ? (
              <span className={`cop-conn ${connected ? "on" : "off"}`} title={connectError || undefined}>
                {connected ? "Conectado" : connectError || "Desconectado"}
              </span>
            ) : WS_URL ? (
              <span className="cop-conn off" title="Copiloto no incluido en tu plan">
                No incluido en tu plan
              </span>
            ) : (
              <span className="cop-conn off">Sin URL WebSocket</span>
            )}
          </div>
          <div className="cop-messages" ref={messagesContainerRef}>
            {isEmpty && (
              <div className="cop-welcome">
                <div className="cop-welcome-icon">
                  <AutoAwesomeIcon sx={{ fontSize: 32, color: "#0066cc" }} />
                </div>
                <h2>Copiloto financiero</h2>
                <p>
                  Consultá tus comprobantes en lenguaje natural. Respuestas con tablas, gráficos y tarjetas.
                </p>
                <SuggestionChips onSelect={send} />
              </div>
            )}
            <MessageList
              messages={messages}
              bottomRef={scrollAnchorRef}
              onRefreshCached={handleRefreshCached}
            />
          </div>
          <div className="cop-input-area">
            <div className="cop-input-wrap">
              <textarea
                className="cop-textarea"
                rows={1}
                value={input}
                placeholder="Preguntá sobre tus documentos…"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                disabled={streaming}
              />
              <button
                type="button"
                className="cop-send-btn"
                onClick={() => send(input)}
                disabled={!input.trim() || streaming}
                aria-label="Enviar"
              >
                <SendIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
            <p className="cop-disclaimer">
              El copiloto solo accede a los documentos de tu organización.
            </p>
          </div>
        </main>
      </div>
      <CopilotUpsellModal
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        plan={plan}
      />
    </div>
  );
}
